import { Modal, Space, Table, TableColumnsType, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import router, { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AutomationActionButton from '../../../../../components/AdminPage/AutomationPage/ActionButton';
import LoadingMenu from '../../../../../components/LoadingMenu';
import MiniTag from '../../../../../components/MiniTag';
import ParagraphWithTooltip from '../../../../../components/ParagraphWithTooltip';
import RefreshButton from '../../../../../components/RefreshButton';
import TableSearchBar from '../../../../../components/TableSearchBar';
import { REFRESH_INTERVAL_DEFAULT } from '../../../../../constants/swr-config';
import useAutomationJobHistoryById from '../../../../../hooks/adminPage/automation/useAutomationJobHistoryById';
import { Automation } from '../../../../../hooks/adminPage/automation/useCurrentAutomation';
import useBulkUserInfo from '../../../../../hooks/filtersPage/useBulkUserInfo';
import useAdminPagesPaginatedUtils from '../../../../../hooks/useAdminPagesPaginatedUtils';
import { DetailedUser, SimpleTicketTab, Ticket, statusi18nKeyMap } from '../../../../../model/types';
import { useTicketTabListInfo } from '../../../../../services/tab';
import { getFormattedDateWithHourWithDayjs, getUserCredentialsToShow } from '../../../../../utils/helpers';
import JobProgress from '../../../../JobFeedbackContainer/components/JobProgress';
import JobStatus from '../../../../JobFeedbackContainer/components/JobStatus';
import { JobStatusTypeMap } from '../../FiltersView/ExportFiltersView/ExportFiltersTable';

enum AutomationJobProgressStage {
    'EXECUTION' = 'EXECUTION',
    'INITIALIZATION' = 'INITIALIZATION'
}
type AutomationJobProgress = `${AutomationJobProgressStage}:${number}`;

interface AutomationJobDataType {
    definition: Automation;
    finishedAt: number;
    id: string;
    logs: string;
    progress: AutomationJobProgress;
    startedAt: number;
    status: JobStatusTypeMap;
    type: string;
    userId: number;
}

const renderAutomationAction =
    (callbackAfterAction?: (automationId: number) => Promise<void>) => (value: any, automation: Automation) => {
        return (
            <AutomationActionButton
                automationDefinition={automation}
                callback={() => callbackAfterAction(automation?.id)}
                runAction
            />
        );
    };
const renderAutomationJobHistoryRefreshAction =
    (callbackAfterAction?: (automationId: number) => Promise<void>) => (value: any, automation: Automation) => {
        return <RefreshButton onRefreshButtonClick={() => callbackAfterAction(automation.id)} />;
    };

const EffectedTicketsModal = ({
    isOpen,
    setIsOpen,
    ticketKeys
}: {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<string[]>>;
    ticketKeys: string[];
}) => {
    const { t } = useTranslation('tables');
    const { t: tCommon } = useTranslation('common');
    const [size, setSize] = useState(10);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const FilteredTicketKeys = ticketKeys.filter((key) => key?.includes(searchTerm));
    const { data: ticketList, isLoading } = useTicketTabListInfo(
        FilteredTicketKeys?.slice((page - 1) * size, size * page)
    );

    const columns: ColumnsType<SimpleTicketTab> = useMemo(
        () => [
            {
                title: t('column.key.label'),
                dataIndex: 'key',
                key: 'key',
                ellipsis: true
            },
            {
                title: t('column.subject.label'),
                dataIndex: 'subject',
                key: 'subject',
                render: (value, ticketInfo) => {
                    return (
                        <Space>
                            <MiniTag mini status={ticketInfo.status.name} centered />
                            {value}
                        </Space>
                    );
                },
                ellipsis: true
            },
            {
                title: t('column.requester.label'),
                dataIndex: 'requester',
                key: 'requester',
                ellipsis: true
            },
            {
                title: t('column.status.label'),
                dataIndex: 'status',
                key: 'status',
                align: 'center',
                render: (value: Ticket['status']) => tCommon(statusi18nKeyMap[value.name])
            },
            {
                title: t('column.channel.label'),
                dataIndex: 'channel',
                key: 'channel',
                align: 'center',
                render: (value: Ticket['status']) => t(`filterTable.channel.${value}`)
            }
        ],
        [t, tCommon]
    );

    const onPaginationChange = (pageIndex: number) => {
        setPage(pageIndex);
    };

    const onShowSizeChange: (current: number, size: number) => void = (current, size) => {
        setSize(size);
    };
    return (
        <Modal
            open={isOpen}
            title={t('affectedTickets')}
            destroyOnClose
            onOk={() => setIsOpen([])}
            onCancel={() => setIsOpen([])}
            cancelButtonProps={{ style: { display: 'none' } }}
            maskClosable={false}
            bodyStyle={{ minHeight: '45vh' }}
            style={{ minWidth: '50%' }}
        >
            <Space direction="vertical" size="middle" className="full-width">
                <TableSearchBar
                    placeholder={t('searchByTicketKey')}
                    searchAction={(v) => {
                        setSearchTerm(v);
                        setPage(1);
                    }}
                    searchTerm={searchTerm}
                    width="350px"
                />
                <Table
                    loading={isLoading}
                    rowKey="key"
                    columns={columns}
                    dataSource={ticketList}
                    pagination={{
                        onChange: onPaginationChange,
                        total: FilteredTicketKeys?.length,
                        pageSize: size,
                        current: page,
                        showSizeChanger: true,
                        showTotal: (total, range) => t('paginationStat', { total, range1: range[0], range2: range[1] }),
                        onShowSizeChange,
                        position: ['bottomCenter'],
                        pageSizeOptions: ['5', '10']
                    }}
                    onRow={(ticket) => {
                        return {
                            onClick: () => router.push(ticket.url) // click row
                        };
                    }}
                    style={{ cursor: 'pointer' }}
                    size="small"
                />
            </Space>
        </Modal>
    );
};

export default function AutomationJobHistoryTableContainer({
    isAutomationsLoading,
    automations,
    totalSize
}: {
    isAutomationsLoading: boolean;
    automations: Automation[];
    totalSize: number;
}) {
    const { t } = useTranslation('admin');
    const { t: tFilters } = useTranslation('filters');
    const {
        query: { automationId, jobId }
    } = useRouter();

    const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([+automationId]);
    const [page, setPage] = useState<number>(1);

    const [effectedTickets, setEffectedTickets] = useState<string[]>([]);
    const {
        handlePagination,
        page: mainTablePage,
        size: mainTableSize,
        sortOrder
    } = useAdminPagesPaginatedUtils('/history');

    const {
        data: automationData,
        mutate: mutateJobData,
        isLoading: isJobDataLoading
    } = useAutomationJobHistoryById(expandedRowKeys.length ? expandedRowKeys[0] : null, page - 1, {
        revalidateIfStale: true,
        refreshInterval: REFRESH_INTERVAL_DEFAULT
    });

    const neededUserIDs = [...new Set(automations?.map(({ userId }) => userId))].join(',');
    const { data: bulkUserInfo, isLoading: isBulkUserInfoLoading } = useBulkUserInfo(
        neededUserIDs ? neededUserIDs : null
    );

    const onExpandedRowsChange = (e: number[]) => {
        setExpandedRowKeys((prev) => e.filter((automationId) => !prev.includes(automationId)));
        setPage(1);
    };
    const handleRunAction = async (automationId: number) => {
        mutateJobData();
        setExpandedRowKeys([automationId]);
        setPage(1);
    };
    const handleRefresh = async (automationId: number) => {
        setExpandedRowKeys([automationId]);
        await mutateJobData();
        setPage(1);
    };
    const onPaginationChange = (pageIndex: number) => {
        setPage(pageIndex);
    };

    const expandedRowRender = () => {
        const columns: TableColumnsType<AutomationJobDataType> = [
            {
                title: t('exportFilter.title.jobId'),
                dataIndex: 'id',
                key: 'jobId',
                fixed: 'left'
            },
            {
                title: t('exportFilter.title.startedAt'),
                dataIndex: 'startedAt',
                key: 'startedAt',
                render: (date: number) => getFormattedDateWithHourWithDayjs(date)
            },
            {
                title: t('exportFilter.title.finishedAt'),
                dataIndex: 'finishedAt',
                key: 'finishedAt',
                render: (date: number) => (date ? getFormattedDateWithHourWithDayjs(date) : null)
            },
            {
                title: t('Automations.affectedTickets'),
                dataIndex: 'ticketKeys',
                key: 'ticketKeys',
                width: '15%',
                fixed: 'right',
                align: 'center',
                render: (tickets: string) => {
                    if (tickets === '' || !tickets || tickets === '""')
                        return <Typography.Text>{t('Automations.noTickets')}</Typography.Text>;
                    //WORKAROUND: old api response has a weird error which is ticket key list coming with double quotes
                    const sanitizedTickets = tickets?.replaceAll('"', '')?.split(',');
                    return (
                        <Space direction="vertical">
                            <Typography.Link onClick={() => setEffectedTickets(sanitizedTickets)}>
                                {t('Automations.ticketCount', {
                                    count: sanitizedTickets?.length
                                })}
                            </Typography.Link>
                        </Space>
                    );
                }
            },
            {
                title: t('exportFilter.title.status'),
                dataIndex: 'status',
                key: 'status',
                fixed: 'right',
                align: 'center',
                width: '15%',
                render: (value: JobStatusTypeMap) => <JobStatus value={value} />
            },
            {
                title: t('exportFilter.title.progress'),
                dataIndex: 'progress',
                key: 'progress',
                fixed: 'right',
                width: '10%',
                render: (value) => {
                    return <JobProgress value={value} />;
                }
            }
        ];

        if (isJobDataLoading || isBulkUserInfoLoading) return <LoadingMenu />;

        return (
            <Table
                rowKey="id"
                columns={columns}
                dataSource={automationData?.content}
                rowClassName={(record) => (record.id === jobId?.toString() ? 'highlight-row' : '')}
                size="small"
                scroll={{ x: 900 }}
                pagination={{
                    current: +page,
                    onChange: onPaginationChange,
                    pageSize: 5,
                    total: automationData?.totalSize,
                    showTotal: (total, range) =>
                        total < 1
                            ? t('Automations.automationJobPagination.paginationStatEmptyJobData', {
                                  total: automationData?.totalSize
                              })
                            : t('Automations.automationJobPagination.paginationStatJobData', {
                                  total: automationData?.totalSize,
                                  range1: range[0],
                                  range2: range[1]
                              }),
                    position: ['bottomCenter']
                }}
            />
        );
    };

    const columns: TableColumnsType<Automation> = [
        {
            key: 'refresh',
            title: '',
            render: renderAutomationJobHistoryRefreshAction(handleRefresh),
            width: '4%',
            fixed: 'left',
            onCell: () => {
                return {
                    onClick: (event) => {
                        event.stopPropagation();
                    }
                };
            }
        },
        {
            key: 'name',
            title: t('Automations.AutomationName'),
            dataIndex: 'name',
            sorter: true,
            sortDirections: ['ascend', 'descend', 'ascend'],
            sortOrder: sortOrder,
            fixed: 'left',
            width: '30%',
            ellipsis: { showTitle: false },
            render: (value) => <ParagraphWithTooltip tooltip={tFilters(value)}>{tFilters(value)}</ParagraphWithTooltip>
        },
        {
            key: 'userId',
            title: t('newAutomation.automationUser'),
            dataIndex: 'userId',
            ellipsis: true,
            width: '35%',
            render: (value: number) => {
                const userInfo = bulkUserInfo?.find((user) => user.id === value);
                return getUserCredentialsToShow(userInfo as DetailedUser, true);
            }
        },
        {
            key: 'action',
            title: t('tableViewContainer.action'),
            render: renderAutomationAction(handleRunAction),
            width: '30%',
            onCell: () => {
                return {
                    onClick: (event) => {
                        event.stopPropagation();
                    }
                };
            }
        }
    ];

    if (isAutomationsLoading) return <LoadingMenu />;

    return (
        <Space size="small" direction="vertical" className="full-width">
            <Table
                columns={columns}
                expandable={{ expandedRowRender, expandedRowKeys, onExpandedRowsChange }}
                dataSource={automations}
                size="small"
                pagination={{
                    current: +mainTablePage,
                    total: totalSize,
                    pageSize: +mainTableSize,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                        t(`paginatedTabs.AUTOMATION.paginationStat`, {
                            total: totalSize,
                            range1: range[0],
                            range2: range[1]
                        }),
                    position: ['bottomCenter'],
                    pageSizeOptions: ['5', '10', '15', '20']
                }}
                rowKey="id"
                scroll={{ x: '1100px' }}
                onChange={handlePagination}
            />
            {!!effectedTickets.length && (
                <EffectedTicketsModal
                    isOpen={!!effectedTickets.length}
                    setIsOpen={setEffectedTickets}
                    ticketKeys={effectedTickets}
                />
            )}
        </Space>
    );
}

export { AutomationJobProgressStage, type AutomationJobDataType, type AutomationJobProgress };
